import { useCompany } from "@/contexts/CompanyContext";

type CompanyRole = "owner" | "admin" | "production_manager" | "sales_rep" | "office_staff" | "subcontractor";

interface Permissions {
  canManageSettings: boolean;
  canManageTeam: boolean;
  canManageIntegrations: boolean;
  canDeleteRecords: boolean;
  canViewAllLeads: boolean;
  canViewReports: boolean;
  canEditPipeline: boolean;
  canViewLeads: boolean;
  canViewEstimates: boolean;
  canViewInvoices: boolean;
  canViewEmailMarketing: boolean;
  canViewSms: boolean;
  canViewStakeholders: boolean;
  canViewTools: boolean;
  canViewCalendar: boolean;
  canViewInbox: boolean;
  canViewTeam: boolean;
  isSubcontractor: boolean;
  role: CompanyRole | null;
  roleLabel: string;
}

const ROLE_LABELS: Record<CompanyRole, string> = {
  owner: "Owner",
  admin: "Admin",
  production_manager: "Event Manager",
  sales_rep: "Sales Rep",
  office_staff: "Office Staff",
  subcontractor: "Freelance Florist",
};

export function usePermissions(): Permissions {
  const { userRole } = useCompany();
  const role = (userRole as CompanyRole) || null;

  const isOwner = role === "owner";
  const isAdmin = role === "admin";
  const isOwnerOrAdmin = isOwner || isAdmin;
  const isPM = role === "production_manager";
  const isSub = role === "subcontractor";

  return {
    canManageSettings: isOwnerOrAdmin,
    canManageTeam: isOwnerOrAdmin,
    canManageIntegrations: isOwnerOrAdmin,
    canDeleteRecords: isOwnerOrAdmin,
    canViewAllLeads: isOwnerOrAdmin || role === "sales_rep",
    canViewReports: isOwnerOrAdmin || isPM,
    canEditPipeline: isOwnerOrAdmin || role === "sales_rep" || isPM,
    canViewLeads: !isSub,
    canViewEstimates: !isSub,
    canViewInvoices: !isSub,
    canViewEmailMarketing: !isSub,
    canViewSms: !isSub,
    canViewStakeholders: !isSub,
    canViewTools: !isSub,
    canViewCalendar: !isSub,
    canViewInbox: !isSub,
    canViewTeam: !isSub,
    isSubcontractor: isSub,
    role,
    roleLabel: role ? ROLE_LABELS[role] || role : "Member",
  };
}
